import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Chip,
  CircularProgress,
  InputBase,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { createMemberTriage, TriageModel } from 'data/api/triage.api';
import { getTriageOptions } from 'data/api/user-note.api';
import { TriageOption, TriageType } from 'data/models/user-note.model';
import { imageRefusal } from 'utils/triage';
import {
  EscalationContacts,
  loadEscalationContacts,
  pickablePeople,
} from '../escalationContacts';
import { CARD, isForbidden, LINK_BUTTON_SX } from './EventDetails';
import { FieldRow, RecipientField } from './RecipientField';
import {
  ERROR_TEXT_SX,
  FieldLabel,
  MessageEditor,
  PRIMARY_BUTTON_SX,
  TriageTakeover,
} from './TriageTakeover';

interface TriageFormValues {
  triagesType: string;
  toIds: string[];
  ccIds: string[];
  subject: string;
  note: string;
}

const typeChipSx = (selected: boolean) => ({
  height: '36px',
  paddingX: '6px',
  fontSize: '14px',
  fontWeight: 600,
  border: '1px solid',
  borderColor: selected ? '#213B54' : '#DBD8D8',
  backgroundColor: selected ? '#213B54' : '#FFFFFF',
  color: selected ? '#FFFFFF' : '#213B54',
  '&&:hover': {
    backgroundColor: selected ? '#16293B' : '#F3F7F7',
  },
  '&.Mui-focusVisible': {
    outline: '2px solid #356766',
    outlineOffset: '2px',
    backgroundColor: selected ? '#213B54' : '#FFFFFF',
  },
});

interface ITriageFormProps {
  /** The member the triage is about. */
  userId: string;
  memberName?: string;
  onCancel: () => void;
  onSent: (triage: TriageModel) => void;
  /** A create for this family is still on the wire from an earlier form. */
  sendInFlight?: boolean;
  onSendingChange?: (sending: boolean) => void;
}

export const TriageForm: React.FC<ITriageFormProps> = ({
  userId,
  memberName,
  onCancel,
  onSent,
  sendInFlight,
  onSendingChange,
}) => {
  const [options, setOptions] = useState<TriageOption[]>([]);
  const [contacts, setContacts] = useState<EscalationContacts>();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>('');
  const [attempt, setAttempt] = useState<number>(0);
  const [sending, setSending] = useState<boolean>(false);
  // An image still being processed is not in the note yet; sending now would
  // send the message without it.
  const [imagesPending, setImagesPending] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TriageFormValues>({
    mode: 'onChange',
    defaultValues: {
      triagesType: '',
      toIds: [],
      ccIds: [],
      subject: '',
      note: '',
    },
  });

  // Everything the form cannot be filled in without. Loaded as one unit so a
  // failure anywhere in it is one visible error with one Try again, rather
  // than a form that looks ready with no types to choose from.
  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadError('');
    setContacts(undefined);
    Promise.all([getTriageOptions(), loadEscalationContacts(userId)])
      .then(([triageOptions, loaded]) => {
        if (!live) return;
        setOptions(triageOptions?.triageOptions || []);
        setContacts(loaded);
        setLoading(false);
      })
      .catch((error) => {
        if (!live) return;
        setLoading(false);
        setLoadError(
          isForbidden(error)
            ? 'You do not have permission to raise a triage for this family.'
            : 'Could not load the triage types and escalation contacts for this family’s organization.',
        );
      });
    return () => {
      live = false;
    };
  }, [userId, attempt]);

  const triagesType = watch('triagesType');
  const people = useMemo(() => pickablePeople(contacts), [contacts]);

  // Choosing a type seeds To and Cc from that category's configured contacts.
  // The rep may edit on top of the defaults, so switching away keeps those
  // edits as that type's draft and switching back restores them.
  const seedKey = contacts && triagesType ? triagesType : '';
  const seededRef = useRef<string>('');
  const draftsRef = useRef<
    Record<string, Pick<TriageFormValues, 'toIds' | 'ccIds'>>
  >({});
  useEffect(() => {
    if (!seedKey || !contacts || seededRef.current === seedKey) return;
    if (seededRef.current) {
      draftsRef.current[seededRef.current] = {
        toIds: getValues('toIds'),
        ccIds: getValues('ccIds'),
      };
    }
    seededRef.current = seedKey;
    const draft = draftsRef.current[seedKey];
    if (draft) {
      setValue('toIds', draft.toIds, { shouldValidate: true });
      setValue('ccIds', draft.ccIds);
      return;
    }
    const forType = contacts.recipients.filter(
      (entry) => entry.triageOptionsId === seedKey,
    );
    const idsFor = (role: 'to' | 'cc') =>
      forType
        .filter((entry) => entry.role === role)
        .map((entry) => entry.user?.userId)
        .filter(Boolean) as string[];
    setValue('toIds', idsFor('to'), { shouldValidate: true });
    setValue('ccIds', idsFor('cc'));
  }, [seedKey, contacts, getValues, setValue]);

  const send = async (values: TriageFormValues) => {
    if (sending || sendInFlight || imagesPending) return;
    setSending(true);
    onSendingChange?.(true);
    try {
      const created = await createMemberTriage({
        associatedToId: userId,
        triagesType: values.triagesType,
        subject: values.subject.trim(),
        toIds: values.toIds,
        ccIds: values.ccIds,
        note: values.note,
        triagesStatus: TriageType.New,
        sendEmail: true,
      });
      onSent(created);
    } catch (error) {
      toast.error(
        isForbidden(error)
          ? 'You do not have permission to raise a triage for this family.'
          : imageRefusal(error) ??
              'The triage could not be created. Nothing was sent.',
      );
      setSending(false);
    } finally {
      onSendingChange?.(false);
    }
  };

  const addressErrors = [errors.toIds?.message, errors.subject?.message].filter(
    Boolean,
  );

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={handleSubmit(send)}
      sx={{ height: '100%', minHeight: 0 }}
    >
      <TriageTakeover
        title="New triage"
        subtitle={memberName ? `For ${memberName}` : undefined}
        backLabel="Back to triages"
        onBack={onCancel}
        // Leaving mid-send would let the rep open a fresh form and raise the
        // same triage twice before the first one lands.
        backDisabled={sending}
        footer={
          <Stack direction="row" alignItems="center" gap={1}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                loading ||
                sending ||
                sendInFlight ||
                imagesPending ||
                !!loadError
              }
              startIcon={
                sending ? (
                  <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
                ) : undefined
              }
              sx={PRIMARY_BUTTON_SX}
            >
              {sending
                ? 'Sending'
                : imagesPending
                ? 'Adding image'
                : 'Send triage'}
            </Button>
            <Button
              onClick={onCancel}
              disabled={sending}
              sx={{
                color: '#213B54',
                textTransform: 'none',
                fontWeight: 600,
                height: '40px',
              }}
            >
              Cancel
            </Button>
          </Stack>
        }
      >
        {!!loadError && (
          <Stack sx={{ gap: '2px', alignItems: 'flex-start' }}>
            <Typography sx={{ fontSize: '13px', color: '#B42318' }}>
              {loadError}
            </Typography>
            <Button
              size="small"
              onClick={() => setAttempt((current) => current + 1)}
              sx={LINK_BUTTON_SX}
            >
              Try again
            </Button>
          </Stack>
        )}

        <Stack sx={{ gap: '8px' }}>
          <FieldLabel required>Triage type</FieldLabel>
          <Controller
            control={control}
            name="triagesType"
            rules={{ required: 'Choose a triage type.' }}
            render={({ field, fieldState }) => (
              <>
                <Stack
                  direction="row"
                  role="radiogroup"
                  aria-label="Triage type"
                  aria-required
                  sx={{ flexWrap: 'wrap', gap: '8px' }}
                >
                  {loading &&
                    [0, 1].map((index) => (
                      <Skeleton
                        key={index}
                        variant="rounded"
                        width={120}
                        height={36}
                        sx={{ borderRadius: '18px' }}
                      />
                    ))}
                  {options.map((option) => {
                    const selected = field.value === option.triageOptionsId;
                    return (
                      <Chip
                        key={option.triageOptionsId}
                        label={option.triageOptionsType}
                        role="radio"
                        aria-checked={selected}
                        disabled={sending}
                        onClick={() => field.onChange(option.triageOptionsId)}
                        sx={typeChipSx(selected)}
                      />
                    );
                  })}
                </Stack>
                {!!fieldState.error && (
                  <Typography sx={ERROR_TEXT_SX}>
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Stack>

        <Stack sx={{ gap: '6px' }}>
          <Stack sx={{ ...CARD, borderColor: '#DBD8D8' }}>
            <Controller
              control={control}
              name="toIds"
              rules={{
                validate: (ids: string[]) =>
                  ids?.length ? true : 'Add at least one recipient.',
              }}
              render={({ field, fieldState }) => (
                <RecipientField
                  label="To"
                  people={people}
                  value={field.value || []}
                  onChange={field.onChange}
                  disabled={loading || sending}
                  invalid={!!fieldState.error}
                  placeholder={
                    triagesType ? 'Add name or email' : 'Choose a type to fill'
                  }
                />
              )}
            />
            <Controller
              control={control}
              name="ccIds"
              render={({ field }) => (
                <RecipientField
                  label="Cc"
                  people={people}
                  value={field.value || []}
                  onChange={field.onChange}
                  disabled={loading || sending}
                  placeholder="Optional"
                  divider
                />
              )}
            />
            <FieldRow label="Subject" divider>
              <Controller
                control={control}
                name="subject"
                rules={{
                  validate: (value: string) =>
                    value?.trim() ? true : 'Add a subject.',
                }}
                render={({ field, fieldState }) => (
                  <InputBase
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    // On the input itself, so a failed submit can focus it.
                    inputRef={field.ref}
                    disabled={sending}
                    placeholder="Short summary"
                    inputProps={{
                      'aria-label': 'Subject',
                      'aria-invalid': !!fieldState.error || undefined,
                    }}
                    sx={{ minHeight: '36px', fontSize: '14px' }}
                  />
                )}
              />
            </FieldRow>
          </Stack>
          {addressErrors.map((message) => (
            <Typography key={message} sx={ERROR_TEXT_SX}>
              {message}
            </Typography>
          ))}
          {!loading && !loadError && !people.length && (
            <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
              This organization has no escalation contacts set up yet, so there
              is no one to send a triage to.
            </Typography>
          )}
        </Stack>

        <Stack sx={{ gap: '8px', flex: 1 }}>
          <FieldLabel required>Message</FieldLabel>
          <MessageEditor
            control={control}
            name="note"
            label="Message"
            requiredMessage="Add a message."
            onImagesPending={setImagesPending}
            fill
            // Locked while sending: the form is torn down on success, so
            // anything typed after Send would be thrown away unsent.
            readOnly={sending}
            minHeight="220px"
            placeholder="Background, concerns, and anything already done"
          />
        </Stack>
      </TriageTakeover>
    </Stack>
  );
};
